import React from 'react';

import Loading from 'components/Loading';
import { CanvasLoading, getContentCanvasCards } from 'components/Skeleton';

import useDelayedFlag from './useDelayedFlag';

/**
 * Shared loading-fallback policy for MetricsGate and VisualisationsGate. Both
 * gates must present the SAME fallback so the VisualisationsGate → MetricsGate
 * handoff reads as one continuous loader — keeping the policy in one place stops
 * the two gates from drifting.
 *
 * - Canvas content (dashboards/reports): a single calm full-canvas placeholder,
 *   shown immediately (not debounced) so the handoff has no blank gap.
 * - Everything else (scorecard/board/goal): a spinner, debounced so a fast
 *   resolve doesn't flash a loader for a frame.
 *
 * Returns `isLoading` (whether to render the fallback instead of children) and
 * the `fallback` node itself (which is `null` during the debounce window).
 */
const useGateLoadingFallback = ({
  contentInput,
  isResolving,
}: {
  contentInput: { contentType?: string; content?: unknown };
  isResolving: boolean;
}): { isLoading: boolean; fallback: React.ReactNode } => {
  const showLoadingUi = useDelayedFlag(isResolving);

  if (!isResolving) {
    return { isLoading: false, fallback: null };
  }

  const cards = getContentCanvasCards(contentInput);
  if (cards.length > 0) {
    return { isLoading: true, fallback: <CanvasLoading /> };
  }

  return { isLoading: true, fallback: showLoadingUi ? <Loading /> : null };
};

export default useGateLoadingFallback;
